
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { BlogPost } from "@/types/blog";
import { importAllToDatabase } from "@/utils/wordpress/database";
import { diagnoseDatabase } from "@/utils/wordpress/diagnostics";
import { toast } from "@/hooks/use-toast";
import { AlertCircle, CheckCircle, Database, RefreshCcw } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ImportResultsProps {
  results: BlogPost[];
  authenticated: boolean;
  onImportStart: () => void;
  onImportComplete: () => void;
}

export const ImportResults = ({ 
  results, 
  authenticated,
  onImportStart,
  onImportComplete 
}: ImportResultsProps) => {
  const [importing, setImporting] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState<any>(null);
  const [importStats, setImportStats] = useState<{
    successCount: number;
    errorCount: number;
    completed: boolean;
  }>({
    successCount: 0,
    errorCount: 0,
    completed: false
  });
  
  const handleImport = async () => {
    if (!authenticated) {
      toast({
        title: "Authentication Required",
        description: "You need to be logged in to import posts",
        variant: "destructive"
      });
      return;
    }
    
    if (results.length === 0) {
      toast({
        title: "No Posts to Import",
        description: "Please fetch posts from WordPress first",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setImporting(true);
      setImportStats({
        successCount: 0,
        errorCount: 0,
        completed: false
      });
      onImportStart();
      
      // Run pre-import diagnostics
      console.log("Running pre-import database diagnostics...");
      const preDiagnostics = await diagnoseDatabase();
      console.log("Pre-import diagnostics:", preDiagnostics);
      
      const { successCount, errorCount, diagnosticResults: postDiagnostics } = await importAllToDatabase(results);
      
      setDiagnosticResults(postDiagnostics);
      setImportStats({
        successCount,
        errorCount,
        completed: true
      });
      
      if (successCount > 0) {
        toast({
          title: "Import Complete",
          description: `Successfully imported ${successCount} posts${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
        });
      } else if (errorCount > 0) {
        toast({
          title: "Import Failed",
          description: `Failed to import all ${errorCount} posts. See console for details.`,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Import error:", error);
      toast({
        title: "Import Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setImporting(false);
      onImportComplete();
    }
  };
  
  const handleRunDiagnostics = async () => {
    try {
      toast({
        title: "Running Diagnostics",
        description: "Checking database for posts..."
      });
      
      const results = await diagnoseDatabase();
      setDiagnosticResults(results);
      
      if (results.success) {
        toast({
          title: "Diagnostics Complete",
          description: `Found ${results.postsCount} posts in the database`
        });
      } else {
        toast({
          title: "Diagnostics Failed",
          description: results.error || "Unknown error",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error running diagnostics:", error);
      toast({
        title: "Diagnostics Error",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  };

  if (results.length === 0) {
    return null;
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>WordPress Import Results</CardTitle>
        <CardDescription>
          Found {results.length} posts from WordPress
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {importStats.completed && (
            <Alert 
              className={importStats.successCount > 0 ? "bg-green-50" : "bg-red-50 border-red-200"}
            >
              <div className="flex items-start gap-3">
                {importStats.successCount > 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                )}
                <div>
                  <AlertTitle>Import Results</AlertTitle>
                  <AlertDescription>
                    {importStats.successCount > 0 ? (
                      <>Successfully imported {importStats.successCount} posts</>
                    ) : (
                      <>No posts were imported successfully</>
                    )}
                    {importStats.errorCount > 0 && (
                      <> with {importStats.errorCount} errors</>
                    )}
                  </AlertDescription>
                </div>
              </div>
            </Alert>
          )}

          <div className="border rounded-md p-4">
            <h3 className="text-sm font-medium mb-2">Sample Posts</h3>
            <ul className="text-sm space-y-1">
              {results.slice(0, 5).map((post, index) => (
                <li key={index}>
                  {post.title} {post.wordpressId && `(ID: ${post.wordpressId})`}
                </li>
              ))}
              {results.length > 5 && (
                <li className="text-muted-foreground">And {results.length - 5} more...</li>
              )}
            </ul>
          </div>
          
          {diagnosticResults && (
            <div className="border rounded-md p-4 bg-slate-50">
              <h3 className="text-sm font-medium mb-2">Database Diagnostics</h3>
              <div className="text-sm space-y-1">
                <p>Success: {diagnosticResults.success ? "Yes" : "No"}</p>
                {diagnosticResults.postsCount !== undefined && (
                  <p>Total posts in database: {diagnosticResults.postsCount}</p>
                )}
                {diagnosticResults.hasWordPressPosts !== undefined && (
                  <p>Has WordPress posts: {diagnosticResults.hasWordPressPosts ? "Yes" : "No"}</p>
                )}
                {diagnosticResults.schema && (
                  <p>Available columns: {Array.isArray(diagnosticResults.schema) ? diagnosticResults.schema.join(', ') : 'Unknown'}</p>
                )}
                {diagnosticResults.error && (
                  <p className="text-red-500">Error: {diagnosticResults.error}</p>
                )}
                {diagnosticResults.samplePosts && diagnosticResults.samplePosts.length > 0 && (
                  <div>
                    <p>Sample post titles:</p>
                    <ul className="list-disc pl-5">
                      {diagnosticResults.samplePosts.map((post: any, i: number) => (
                        <li key={i}>{post.title || 'Untitled'}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex gap-2 justify-between">
        <div>
          <Button 
            variant="outline" 
            onClick={handleRunDiagnostics}
            disabled={importing}
            className="flex items-center gap-2"
          >
            <Database className="w-4 h-4" />
            Run Database Diagnostics
          </Button>
        </div>
        <div>
          {authenticated ? (
            <Button 
              onClick={handleImport} 
              disabled={importing || results.length === 0}
              className={importing ? "opacity-50 cursor-not-allowed flex items-center gap-2" : "flex items-center gap-2"}
            >
              {importing ? (
                <>
                  <RefreshCcw className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <RefreshCcw className="w-4 h-4" />
                  Import {results.length} Posts
                </>
              )}
            </Button>
          ) : (
            <Button disabled>
              Login Required to Import
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
